import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  mapEntities,
  mapEntityTypeConfig,
  mapDashboardStats,
  mapScoreActions,
  mapCountries,
  getLiveMapEntities,
  getTopScoreMapEntities,
  getTrendingMapEntities,
  formatMapCount,
  type MapEntity,
  type MapEntityType,
} from '@/mocks/rfwMap';

function MapEntityCard({ entity, compact }: { entity: MapEntity; compact?: boolean }) {
  const config = mapEntityTypeConfig[entity.type];

  if (compact) {
    return (
      <Link
        to={entity.linkUrl}
        className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 group"
      >
        <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
          <img src={entity.imageUrl} alt={entity.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-xs font-bold text-foreground-900 truncate">{entity.name}</p>
            {entity.isLive && (
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse shrink-0"></span>
            )}
          </div>
          <p className="text-[10px] text-foreground-500 truncate">{entity.city}, {entity.country}</p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${config.bgColor} ${config.textColor} shrink-0`}>
          {config.label}
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={entity.linkUrl}
      className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group"
    >
      <div className="relative">
        <img src={entity.imageUrl} alt={entity.name} className="w-full h-36 object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        {entity.isLive && (
          <span className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            EN VIVO
          </span>
        )}
        <span className={`absolute bottom-3 left-3 px-2 py-0.5 rounded-full text-[9px] font-semibold ${config.bgColor} ${config.textColor} backdrop-blur-sm`}>
          <i className={`${config.icon} text-[9px] mr-1`}></i>
          {config.label}
        </span>
        <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-800 text-[9px] font-semibold backdrop-blur-sm">
          {entity.score} pts
        </span>
      </div>
      <div className="p-4">
        <p className="text-sm font-bold text-foreground-900 leading-snug mb-0.5 line-clamp-1">{entity.name}</p>
        <p className="text-[11px] text-foreground-500 mb-3 line-clamp-1">{entity.subtitle}</p>
        <div className="flex items-center gap-2 text-[10px] text-foreground-400 mb-2">
          <i className="ri-map-pin-line"></i>
          <span>{entity.city}, {entity.country}</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="flex items-center gap-1 text-foreground-500">
            <i className="ri-group-line"></i>
            {formatMapCount(entity.totalFollowers)}
          </span>
          <span className={`flex items-center gap-1 ${entity.trend === 'up' ? 'text-emerald-600' : 'text-foreground-400'}`}>
            <i className={`${entity.trend === 'up' ? 'ri-arrow-up-line' : 'ri-subtract-line'}`}></i>
            +{entity.trendPercent}%
          </span>
        </div>
        {entity.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-background-200/70">
            {entity.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[8px] font-medium">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function MapaSection() {
  const [activeTab, setActiveTab] = useState<'live' | 'top' | 'trending'>('live');
  const stats = mapDashboardStats;

  const liveEntities = getLiveMapEntities().slice(0, 8);
  const topEntities = getTopScoreMapEntities(8);
  const trendingEntities = getTrendingMapEntities(8);

  const tabs = [
    { id: 'live' as const, label: 'En Vivo Ahora', icon: 'ri-live-line', count: liveEntities.length, color: 'text-rose-600' },
    { id: 'top' as const, label: 'Top Score', icon: 'ri-trophy-line', count: topEntities.length, color: 'text-amber-600' },
    { id: 'trending' as const, label: 'En Tendencia', icon: 'ri-fire-line', count: trendingEntities.length, color: 'text-emerald-600' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
            SISTEMA DE MAPA RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Descubrí la <span className="text-primary-600">Comunidad Global</span> RFW
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Radios, locutores, eventos, clubes y oyentes — todo geolocalizado en un mapa interactivo. Filtrá por país, categoría e idioma y descubrí qué está pasando cerca tuyo.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Entidades Mapeadas', value: stats.totalEntities, icon: 'ri-map-pin-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Países', value: stats.totalCountries, icon: 'ri-global-line', color: 'text-accent-600 bg-accent-50' },
            { label: 'Ciudades', value: stats.totalCities, icon: 'ri-building-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Activos Ahora', value: stats.activeNow, icon: 'ri-pulse-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Oyentes Totales', value: formatMapCount(stats.totalListeners), icon: 'ri-headphone-line', color: 'text-amber-600 bg-amber-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Map Embed */}
        <div className="relative w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden mb-10">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d0!2d-75!3d5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNcKwMDAnMDAuMCJOIDc1wrAwMCcwMC4wIlc!5e0!3m2!1ses!2sar!4v1"
            className="absolute inset-0 w-full h-full"
            style={{ border: 0, filter: 'saturate(0.8) brightness(0.85)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Mapa Interactivo RFW"
          ></iframe>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>
          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
            {(['radio','locutor','evento','club','oyente'] as MapEntityType[]).map((type) => {
              const config = mapEntityTypeConfig[type];
              return (
                <span key={type} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-background-50/90 backdrop-blur-sm border border-background-200/50 ${config.textColor}`}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: config.markerColor }}></span>
                  {config.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm cursor-pointer'
                    : 'text-foreground-600 hover:text-foreground-900 cursor-pointer'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(activeTab === 'live' ? liveEntities : activeTab === 'top' ? topEntities : trendingEntities).map((entity) => (
            <MapEntityCard key={entity.id} entity={entity} />
          ))}
        </div>

        {/* Nearby Entities — compact list */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          {getTopScoreMapEntities(6).map((entity) => (
            <MapEntityCard key={`compact-${entity.id}`} entity={entity} compact />
          ))}
        </div>

        {/* Score Section */}
        <div className="mt-10 p-6 rounded-2xl bg-background-50 border border-background-200/70">
          <div className="text-center mb-5">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-flashlight-line text-lg text-primary-600"></i>
            </div>
            <h3 className="text-sm font-bold text-foreground-950">Score por Actividad Local</h3>
            <p className="text-xs text-foreground-500 mt-1">Cada acción local suma puntos. Mantenete activo en tu ciudad y crecé en el mapa.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {mapScoreActions.slice(0, 6).map((action, i) => (
              <div key={i} className="p-3 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-8 h-8 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-1.5">
                  <i className={`${action.icon} ${action.color} text-sm`}></i>
                </div>
                <p className="text-[11px] font-bold text-foreground-900 mb-0.5">{action.action}</p>
                <p className="text-sm font-extrabold text-primary-600">+{action.points} pts</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/mapa-rfw"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-map-pin-line"></i>
            Explorar el Mapa Interactivo
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-compass-3-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            El mapa debe ayudar a descubrir radios.
          </p>
          <p className="text-xs text-foreground-600 max-w-xl mx-auto">
            Cada pin en el mapa es una radio, un locutor, un evento, un club o un oyente real. El mapa RFW no es decoración — es una herramienta de descubrimiento. Encontrá qué está pasando cerca tuyo, descubrí nuevas emisoras, conectate con comunidades locales y expandí tu red radial sin fronteras.
          </p>
        </div>
      </div>
    </section>
  );
}